// Pure builder for the official JIGS SFX Grading Scheme application form.
//
// Returns a pdfmake document-definition object (a plain JS object). This module
// intentionally has NO dependency on pdfmake itself: it only produces the
// declarative doc-def that pdfmake later renders. Keeping it pure makes it
// unit-testable (no font VFS, no DOM, no async) and means the lay-out can be
// reasoned about as data.
//
// pdfmake renders this doc-def to REAL vector text (each glyph is drawn from an
// embedded TrueType font), so the resulting PDF is fully selectable, copyable
// and searchable — unlike the html2canvas path used by SharePdfButton, which
// rasterises the DOM to a bitmap image and embeds that.

import {
  DISCIPLINE_LABELS,
  GRADE_LABELS,
  GRADING_REQUIREMENTS,
  QUALIFICATIONS,
} from '../constants/grading';
import { GRADING_PDF_COLORS } from '../constants/colors';

// ── Palette / shared style tokens ──────────────────────────────────
// JIGS form uses an orange heading band. We approximate with a warm orange.
// Hex values live in constants/colors.js (GRADING_PDF_COLORS) alongside the
// other Crew Forge chart-hex palettes — aliased locally for readability.
const ORANGE = GRADING_PDF_COLORS.orange;
const ORANGE_SOFT = GRADING_PDF_COLORS.orangeSoft;
const GREY_RULE = GRADING_PDF_COLORS.greyRule;
const LABEL_GREY = GRADING_PDF_COLORS.labelGrey;

// The discipline columns shown in the Section 2/3 grade grids. The data model
// is single-discipline (profile.discipline ∈ physical|pyrotechnic|visual|snow),
// so only the applicant's active discipline column ever carries an "X" — the
// others render blank but are shown so the form matches the real four-column
// JIGS layout.
//
// The official form's 4th column is "Subsidiary SFX (plus subsidiary title)".
// In our data model the snow discipline occupies that slot, so the column id
// is 'snow' (it MUST match activeDiscipline so a snow applicant still gets a
// markable cell) while the label reflects the official "Snow / Subsidiary"
// heading. The accompanying subsidiary_title free-text is rendered separately
// in Section 3.
const DISCIPLINE_COLUMNS = [
  { id: 'physical', label: 'Physical' },
  { id: 'pyrotechnic', label: 'Pyrotechnic' },
  { id: 'visual', label: 'Visual' },
  { id: 'snow', label: 'Snow / Subsidiary' },
];

// Grade rows for the grid (includes the "None" row that the official form has).
const GRADE_ROWS = [
  { id: '', label: 'None' },
  { id: 'trainee', label: 'Trainee' },
  { id: 'technician', label: 'Technician' },
  { id: 'senior_technician', label: 'Senior Technician' },
  { id: 'supervisor', label: 'Supervisor' },
];

// Shared pdfmake table layout for the bordered grids (grade grid, CV table,
// qualifications, references, publication). Defined once so the 0.5pt grey
// rule never drifts between tables. Use this as the `layout` for any bordered
// grid in this module.
const GRID_LAYOUT = {
  hLineColor: () => GREY_RULE,
  vLineColor: () => GREY_RULE,
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
};

// ── Small helpers ───────────────────────────────────────────────────

/**
 * Build an orange-filled bold header cell for a table column heading.
 * Pass `extra` to merge additional cell props (e.g. { alignment: 'center' }).
 */
function hdr(text, extra) {
  return { text, bold: true, fontSize: 8, fillColor: ORANGE_SOFT, ...(extra || {}) };
}

/** Coerce anything to a trimmed display string; null/undefined → ''. */
function s(v) {
  if (v == null) return '';
  return String(v);
}

/** Display value or an em-dash placeholder so empty cells never show blank-noise. */
function val(v) {
  const str = s(v).trim();
  return str === '' ? '—' : str;
}

/** Format an ISO-ish date string as dd MMM yyyy (en-GB); blank-safe. */
function fmtDate(dateStr) {
  const str = s(dateStr).trim();
  if (!str) return '—';
  // Parse defensively. Accept yyyy-mm-dd and full ISO. Never throw.
  const d = new Date(str.length <= 10 ? str + 'T00:00:00' : str);
  if (Number.isNaN(d.getTime())) return str; // show raw if unparseable
  try {
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return str;
  }
}

/** Map a grade id to its human label; '' / unknown → as-given or em-dash. */
function gradeLabel(gradeId) {
  const id = s(gradeId).trim();
  if (!id) return '—';
  return GRADE_LABELS[id] || id;
}

/** Defensive parse of a value that may be an object or a JSON string. */
function parseObj(v, fallback) {
  if (v == null) return fallback;
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return parsed == null ? fallback : parsed;
    } catch {
      return fallback;
    }
  }
  return v;
}

// ── Re-usable doc-def fragments ─────────────────────────────────────

/** An orange section heading bar with white bold text. */
function sectionHeading(text) {
  return {
    table: {
      widths: ['*'],
      body: [[{ text, color: 'white', bold: true, fontSize: 11, margin: [4, 3, 4, 3] }]],
    },
    layout: {
      fillColor: () => ORANGE,
      hLineWidth: () => 0,
      vLineWidth: () => 0,
    },
    margin: [0, 10, 0, 4],
  };
}

/** A two-column label/value line used through the applicant-detail sections. */
function labelValueRows(pairs) {
  return {
    table: {
      widths: [150, '*'],
      body: pairs.map(([label, value]) => [
        { text: label, color: LABEL_GREY, fontSize: 9, margin: [0, 2, 0, 2] },
        { text: value, fontSize: 10, margin: [0, 2, 0, 2] },
      ]),
    },
    layout: {
      hLineColor: () => GREY_RULE,
      vLineWidth: () => 0,
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0 : 0.5),
    },
    margin: [0, 0, 0, 4],
  };
}

/**
 * Build the discipline × grade grid used for Section 2 (currently held) and
 * Section 3 (applied for). Marks an "X" where the active discipline column
 * meets the supplied grade row.
 */
export function gradeGrid(activeDiscipline, markedGrade) {
  const headerRow = [
    { text: '', border: [false, false, false, false] },
    ...DISCIPLINE_COLUMNS.map((c) => hdr(c.label, { alignment: 'center' })),
  ];

  const bodyRows = GRADE_ROWS.map((row) => {
    const cells = [
      { text: row.label, fontSize: 9, margin: [2, 1, 2, 1] },
    ];
    for (const col of DISCIPLINE_COLUMNS) {
      const isMark = col.id === activeDiscipline && row.id === s(markedGrade).trim();
      cells.push({
        text: isMark ? 'X' : '',
        bold: true,
        alignment: 'center',
        fontSize: 11,
        margin: [0, 1, 0, 1],
      });
    }
    return cells;
  });

  return {
    table: {
      widths: [110, '*', '*', '*', '*'],
      body: [headerRow, ...bodyRows],
    },
    layout: GRID_LAYOUT,
    margin: [0, 0, 0, 6],
  };
}

/** A small text-line stub for free-text / blank-line fields. */
function blankLines(count) {
  const lines = [];
  for (let i = 0; i < count; i++) {
    lines.push({
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: GREY_RULE }],
      margin: [0, 12, 0, 0],
    });
  }
  return lines;
}

// ── The builder ─────────────────────────────────────────────────────

/**
 * Build the pdfmake document definition for the JIGS SFX grading application.
 *
 * PURE: does not read the clock or any global state. `generatedDate` must be
 * supplied by the caller (the button computes it via new Date()).
 *
 * @param {Object} profile        grading profile (full_name, discipline, grades, …)
 * @param {Array<Object>} entries  experience rows
 * @param {Object} progress        computed progress summary (computeGradingProgress)
 * @param {string} [generatedDate] e.g. '28/05/2026' — used as the application date
 * @returns {Object} pdfmake document definition
 */
export function buildGradingFormDoc(profile, entries, progress, generatedDate, hasPhoto) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const rows = Array.isArray(entries) ? entries : [];
  const prog = progress && typeof progress === 'object' ? progress : {};
  const appDate = s(generatedDate).trim() || '—';

  const discipline = s(p.discipline).trim() || 'physical';
  const quals = parseObj(p.qualifications, {}) || {};
  const publishPerms = parseObj(p.publish_permissions, {}) || {};
  const signatures = (() => {
    const sigs = parseObj(p.supervisor_signatures, []);
    return Array.isArray(sigs) ? sigs : [];
  })();

  // Years with SFX: prefer the computed total; round to a whole number.
  const totalYears =
    typeof prog.totalYears === 'number' && Number.isFinite(prog.totalYears)
      ? Math.round(prog.totalYears)
      : '';

  const content = [];

  // ── PAGE 1: Applicant details + grades held / applied for ──────────
  // The passport photo is NOT embedded here via pdfmake — pdfmake's image
  // embedder triggered intermittent buffer-extraction stalls in 0.3.x.
  // Instead the export pipeline in GradingExportButton uses pdf-lib to
  // DRAW the photo onto page 1 AFTER pdfmake renders the base PDF.
  //
  // Layout: when `hasPhoto` is true, Section 1 renders as a 2-column
  // pdfmake `columns` block — heading + labelValueRows stacked in the left
  // column, a 130pt empty column on the right. The pdf-lib photo overlay
  // lands in that right column at the top of page 1.
  const section1Details = [
    ['Full name', val(p.full_name)],
    ['Date of birth', fmtDate(p.date_of_birth)],
    ['Address', val(p.address)],
    ['Post code', val(p.post_code)],
    ['Email', val(p.email)],
    ['Phone (mobile)', val(p.phone)],
    ['Application date', appDate],
  ];
  if (hasPhoto) {
    content.push({
      columns: [
        {
          width: '*',
          stack: [
            sectionHeading('Section 1 — Applicant details'),
            labelValueRows(section1Details),
          ],
        },
        { width: 130, text: '' },
      ],
      columnGap: 8,
    });
  } else {
    content.push(sectionHeading('Section 1 — Applicant details'));
    content.push(labelValueRows(section1Details));
  }

  content.push(sectionHeading('Section 2 — Grade(s) currently held'));
  content.push({
    text: `Discipline: ${DISCIPLINE_LABELS[discipline] || discipline}`,
    fontSize: 9,
    color: LABEL_GREY,
    margin: [0, 0, 0, 4],
  });
  content.push(gradeGrid(discipline, p.current_grade));

  content.push(sectionHeading('Section 3 — Grade(s) applied for'));
  content.push(gradeGrid(discipline, p.grade_applied_for));
  if (s(p.subsidiary_title).trim()) {
    content.push({
      text: `Subsidiary title: ${val(p.subsidiary_title)}`,
      fontSize: 9,
      margin: [0, 0, 0, 4],
    });
  }

  // ── PAGE 2: Service dates + pyro experience ────────────────────────
  content.push(sectionHeading('Sections 4–7 — Service record'));
  content.push(
    labelValueRows([
      ['4. Date of last grading', fmtDate(p.date_last_grading)],
      ['5. Date of entry to industry', fmtDate(p.date_entered_industry)],
      ['6. Years with SFX', totalYears === '' ? '—' : String(totalYears)],
      [
        '7. Time in pyrotechnics',
        (() => {
          const y = Number(p.pyro_years) || 0;
          const m = Number(p.pyro_months) || 0;
          if (!y && !m) return '—';
          return `${y} year(s) ${m} month(s)`;
        })(),
      ],
    ])
  );
  content.push({
    text: 'Totals derived from the experience record below (5-day weeks).',
    fontSize: 8,
    italics: true,
    color: LABEL_GREY,
    margin: [0, 2, 0, 4],
  });
  content.push(
    labelValueRows([
      ['Total 5-day weeks', String(prog.totalWeeks != null ? prog.totalWeeks : 0)],
      ['Total days', String(prog.totalDays != null ? prog.totalDays : 0)],
      ['Supervisor references on file', String(prog.signatureCount != null ? prog.signatureCount : signatures.length)],
    ])
  );

  // ── PAGE 3: General CV / productions ───────────────────────────────
  content.push({ text: '', pageBreak: 'before' });
  content.push(sectionHeading('Section 8 — General CV / productions'));
  {
    const header = [
      hdr('Production title'),
      hdr('Supervisor'),
      hdr('Credited grade'),
      hdr('Dates'),
    ];
    const body = [header];
    if (rows.length === 0) {
      body.push([
        { text: 'No productions recorded.', italics: true, color: LABEL_GREY, fontSize: 9, colSpan: 4 },
        {},
        {},
        {},
      ]);
    } else {
      for (const e of rows) {
        const r = e || {};
        const dates =
          s(r.start_date).trim() || s(r.end_date).trim()
            ? `${fmtDate(r.start_date)} – ${fmtDate(r.end_date)}`
            : '—';
        body.push([
          { text: val(r.production_name), fontSize: 9 },
          { text: val(r.supervisor_name), fontSize: 9 },
          { text: gradeLabel(r.credited_grade), fontSize: 9 },
          { text: dates, fontSize: 9 },
        ]);
      }
    }
    content.push({
      table: { headerRows: 1, widths: ['*', '*', 90, 120], body },
      layout: GRID_LAYOUT,
      margin: [0, 0, 0, 6],
    });
  }

  // ── PAGE 4: Qualifications + RAs + other experience ────────────────
  content.push({ text: '', pageBreak: 'before' });
  content.push(sectionHeading('Section 9 — Formal qualifications'));
  {
    const header = [
      hdr('Qualification'),
      hdr('Held', { alignment: 'center' }),
    ];
    const body = [header];
    for (const q of QUALIFICATIONS) {
      body.push([
        { text: q.name, fontSize: 9 },
        { text: quals[q.id] ? 'YES' : 'NO', bold: true, fontSize: 9, alignment: 'center' },
      ]);
    }
    content.push({
      table: { headerRows: 1, widths: ['*', 50], body },
      layout: GRID_LAYOUT,
      margin: [0, 0, 0, 6],
    });
    content.push({
      text: 'Other qualifications:',
      fontSize: 9,
      color: LABEL_GREY,
      margin: [0, 2, 0, 1],
    });
    content.push({ text: val(p.other_qualifications), fontSize: 10, margin: [0, 0, 0, 6] });
  }

  content.push(sectionHeading('Section 10 — Risk assessments'));
  // Risk assessments are only required for Senior Technician and Supervisor
  // grades (riskAssessments > 0). Technician/Trainee applications don't need them.
  const raRequired =
    (((GRADING_REQUIREMENTS[discipline] || {})[s(p.grade_applied_for).trim()] || {}).riskAssessments) || 0;
  if (raRequired > 0) {
    content.push({
      text: 'Two example risk assessments are required to support this application (see attached).',
      fontSize: 9,
      margin: [0, 0, 0, 2],
    });
    content.push(...blankLines(2));
  } else {
    content.push({
      text: 'Not required for the grade applied for.',
      fontSize: 9,
      italics: true,
      color: '#6b7280',
      margin: [0, 0, 0, 2],
    });
  }

  content.push(sectionHeading('Section 11 — Other relevant experience'));
  content.push({ text: val(p.other_experience), fontSize: 10, margin: [0, 4, 0, 6] });

  // ── PAGE 5: Section 11a — experience detail blocks + declaration ───
  content.push({ text: '', pageBreak: 'before' });
  content.push(sectionHeading('Section 11a — Experience details'));
  if (rows.length === 0) {
    content.push({
      text: 'No experience entries recorded.',
      italics: true,
      color: LABEL_GREY,
      fontSize: 9,
      margin: [0, 0, 0, 6],
    });
  } else {
    rows.forEach((e, idx) => {
      const r = e || {};
      content.push({
        text: `Production ${idx + 1}`,
        bold: true,
        fontSize: 10,
        margin: [0, idx === 0 ? 0 : 8, 0, 2],
      });
      content.push(
        labelValueRows([
          ['Production name', val(r.production_name)],
          ['Start date', fmtDate(r.start_date)],
          ['End date', fmtDate(r.end_date)],
          ['SFX supervisor', val(r.supervisor_name)],
          ['Your credited grade', gradeLabel(r.credited_grade)],
          ['No. of 5-day weeks', String(Number(r.weeks_worked) || 0)],
          ['Odd days', String(Number(r.odd_days) || 0)],
          ['Total days', String(Number(r.total_days) || 0)],
          ['Effects & materials / techniques', val(r.effects_techniques)],
        ])
      );
      if (s(r.notes).trim()) {
        content.push({ text: `Notes: ${val(r.notes)}`, fontSize: 9, italics: true, margin: [0, 0, 0, 2] });
      }
    });
  }

  // Declaration block.
  content.push(sectionHeading('Declaration'));
  content.push({
    text: 'The above information is true and accurate to the best of my knowledge.',
    fontSize: 10,
    margin: [0, 2, 0, 8],
  });
  content.push(
    labelValueRows([
      ['Signed', ' '],
      ['Print name', val(p.full_name)],
      ['Date', appDate],
    ])
  );

  // Supervisor references supporting the 3-reference requirement. References
  // now live one-per-production: each experience entry's supervisor_name IS the
  // reference name, with the entry's reference_title as the role.
  content.push(sectionHeading('Supervisor references'));
  {
    const approvalLabel = (st) =>
      st === 'approved' ? 'Approved ✓' : st === 'pending' ? 'Pending' : st === 'rejected' ? 'Declined' : '—';
    const header = [
      hdr('Name'),
      hdr('Title'),
      hdr('Email'),
      hdr('Confirmed?'),
      hdr('Production'),
    ];
    const body = [header];
    const refRows = rows.filter((e) => s(e && e.supervisor_name).trim() !== '');
    if (refRows.length === 0) {
      body.push([
        { text: 'No supervisor references recorded.', italics: true, color: LABEL_GREY, fontSize: 9, colSpan: 5 },
        {},
        {},
        {},
        {},
      ]);
    } else {
      for (const e of refRows) {
        const r = e || {};
        body.push([
          { text: val(r.supervisor_name), fontSize: 9 },
          { text: val(r.reference_title), fontSize: 9 },
          { text: val(r.reference_email), fontSize: 9 },
          { text: approvalLabel(r.approval_status), fontSize: 9 },
          { text: val(r.production_name), fontSize: 9 },
        ]);
      }
    }
    content.push({
      table: { headerRows: 1, widths: ['*', 100, '*', 70, '*'], body },
      layout: GRID_LAYOUT,
      margin: [0, 0, 0, 6],
    });
  }

  // ── PAGE 6: Section 12 — publication permission ────────────────────
  content.push({ text: '', pageBreak: 'before' });
  content.push(sectionHeading('Section 12 — Applicant permission for publication'));
  content.push({
    text:
      'I give permission for the following contact details to be published in the graded crew directory:',
    fontSize: 9,
    margin: [0, 0, 0, 4],
  });
  {
    const fields = [
      { id: 'name', label: 'Name' },
      { id: 'address', label: 'Address' },
      { id: 'mobile', label: 'Mobile' },
      { id: 'email', label: 'Email' },
      { id: 'phone', label: 'Phone' },
    ];
    const header = [
      hdr('Contact detail'),
      hdr('Publish?', { alignment: 'center' }),
    ];
    const body = [header];
    for (const f of fields) {
      body.push([
        { text: f.label, fontSize: 9 },
        { text: publishPerms[f.id] ? 'YES' : 'NO', bold: true, fontSize: 9, alignment: 'center' },
      ]);
    }
    content.push({
      table: { headerRows: 1, widths: ['*', 60], body },
      layout: GRID_LAYOUT,
      margin: [0, 0, 0, 6],
    });
  }

  return {
    pageSize: 'A4',
    pageMargins: [40, 70, 40, 50],
    info: {
      title: 'JIGS SFX Grading Application',
      author: s(p.full_name) || 'Applicant',
      subject: 'Joint Industry SFX Grading Scheme — Application for grading/re-grading',
    },
    // Running header on every page (real text, repeated by pdfmake).
    header: () => ({
      columns: [
        {
          text: 'Joint Industry SFX Grading Scheme — Application for grading/re-grading',
          fontSize: 9,
          bold: true,
          color: ORANGE,
          margin: [40, 25, 40, 0],
        },
      ],
    }),
    footer: (currentPage, pageCount) => ({
      columns: [
        { text: appDate === '—' ? '' : `Generated ${appDate}`, fontSize: 7, color: LABEL_GREY, margin: [40, 0, 0, 0] },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7,
          color: LABEL_GREY,
          alignment: 'right',
          margin: [0, 0, 40, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 10, color: '#1a1a1a' },
    content,
  };
}

export default buildGradingFormDoc;
